import {
  type ProfileLayoutScreenModel,
  type ScreenType,
} from "@package/ui-gql/index-data-access";
import { useSelectorDisplay } from "@package/ui-redux";
import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type ScreenDimension } from "../../constants/constants";
import { getWallDimensions } from "../../utils/get-screen-dimensions";

type AutoZoomFrameProps = {
  type: ScreenType;
  profileScreen?: ProfileLayoutScreenModel;
  height?: number;
};

export function AutoZoomFrame({
  children,
  type,
  profileScreen,
  height,
}: PropsWithChildren<AutoZoomFrameProps>) {
  const fullWidth = useSelectorDisplay((state) => state.display.fullWidth);

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const dimensions = useMemo<ScreenDimension | undefined>(
    () => (profileScreen ? getWallDimensions(type, profileScreen) : undefined),
    [profileScreen, type],
  );

  const zoom = useMemo<number>(() => {
    let zoomWidth = 1;
    let zoomHeight = 1;

    if (
      containerWidth &&
      dimensions?.width &&
      dimensions.width > containerWidth
    ) {
      zoomWidth = containerWidth / dimensions.width || 1;
    }

    if (height && dimensions?.height && dimensions.height > height) {
      zoomHeight = height / dimensions.height || 1;
    }

    return zoomWidth < zoomHeight ? zoomWidth : zoomHeight;
  }, [containerWidth, dimensions?.width, dimensions?.height, height]);

  const updateContainerWidth = useCallback(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  }, []);

  useEffect(
    function effectOnFrameResized() {
      window.addEventListener("resize", updateContainerWidth);

      return () => {
        window.removeEventListener("resize", updateContainerWidth);
      };
    },
    [updateContainerWidth],
  );

  useEffect(
    function effectOnContainerWidthFnChanged() {
      updateContainerWidth();
    },
    [fullWidth, profileScreen, type, updateContainerWidth],
  );

  return (
    <div ref={containerRef} className="grow min-w-0">
      <div
        className="relative"
        style={{
          ...dimensions,
          zoom,
        }}
      >
        {children}
      </div>
    </div>
  );
}
